import React, { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import type * as Plotly from "plotly.js";
import { Button, HTMLSelect, Slider } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ApiGraphData } from "./SimulationResults";
import { AXIS_TEXT_COLOR, GRID_COLOR, usePlotResize, ZERO_LINE_COLOR } from "./SimulationGraph";
import { AxisSelection, AxisTrace, getAxisTitle, sampleAxes, TIME_AXIS } from "./signalAxes";

export type PlayerAxis = "x" | "y";

export const PLAYER_AXES: PlayerAxis[] = ["x", "y"];

// The first signal over time.
export const getDefaultPlayerAxes = (options: string[]): AxisSelection<PlayerAxis> => [{
  x: TIME_AXIS,
  y: options[1] ?? TIME_AXIS,
}];

// Playback speed as simulated seconds per wall-clock second.
const SPEEDS = [0.1, 0.25, 0.5, 1, 2, 5, 10, 50, 100];
const SLIDER_STEPS = 1000;

// Past the palette the dots repeat their colour with another shape, as the
// lines repeat it with another dash.
const DOT_SYMBOLS: Record<string, string> = { solid: "circle", dash: "square", dot: "diamond" };
const PATH_OPACITY = 0.2;

// Index of the last sample at or before `time`: the values in effect then.
const sampleIndexAt = (seconds: number[], time: number): number => {
  let low = 0;
  let high = seconds.length - 1;

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (seconds[mid] <= time) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  return low;
};

const formatTime = (time: number) => `${Number(time.toPrecision(6))} s`;

interface SimulationPlayerProps {
  data: ApiGraphData;
  selection: AxisSelection<PlayerAxis>;
  traces: AxisTrace<PlayerAxis>[];
}

// Replays the simulation as dots moving through the X–Y plane: every frame is
// the chosen signals' values at the current time, over their faint full paths.
const SimulationPlayer: React.FC<SimulationPlayerProps> = ({ data, selection, traces }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  usePlotResize(wrapperRef);

  const sampled = useMemo(() => traces.map((trace) => {
    // Every simulation step, so the dots move through the steps where nothing
    // changed instead of jumping between change points.
    const { seconds, columns } = sampleAxes(data, [trace.axes.x, trace.axes.y], true);
    return { trace, seconds, xValues: columns.get(trace.axes.x) ?? [], yValues: columns.get(trace.axes.y) ?? [] };
  }), [data, traces]);

  const allSeconds = sampled.flatMap((item) => item.seconds.length > 0 ? [item.seconds[0], item.seconds[item.seconds.length - 1]] : []);
  const start = allSeconds.length > 0 ? Math.min(...allSeconds) : 0;
  const end = allSeconds.length > 0 ? Math.max(...allSeconds) : 0;

  const [time, setTime] = useState(start);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  // Stay inside the timeline when a new run or other axes change it.
  const currentTime = Math.min(Math.max(time, start), end);

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let animationFrame: number;
    let lastFrame = performance.now();

    const step = (now: number) => {
      const elapsed = (now - lastFrame) / 1000;
      lastFrame = now;

      setTime((prevTime) => {
        const nextTime = Math.min(Math.max(prevTime, start) + elapsed * speed, end);
        if (nextTime >= end) {
          setIsPlaying(false);
        }
        return nextTime;
      });

      animationFrame = window.requestAnimationFrame(step);
    };

    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [isPlaying, speed, start, end]);

  const togglePlaying = () => {
    // Playing from the end starts over.
    if (!isPlaying && currentTime >= end) {
      setTime(start);
    }
    setIsPlaying(!isPlaying);
  };

  // The full paths stay put, so they keep their array references between
  // frames and Plotly only redraws the trails and the dots.
  const pathTraces = useMemo<Partial<Plotly.ScatterData>[]>(() => sampled.map(({ trace, seconds, xValues, yValues }) => ({
    type: "scatter",
    mode: "lines",
    name: trace.name,
    x: xValues,
    y: yValues,
    customdata: seconds,
    line: { color: trace.color, width: 2, dash: trace.dash },
    opacity: PATH_OPACITY,
    hovertemplate: `${trace.axes.x}: %{x}<br>${trace.axes.y}: %{y}<br>t = %{customdata} s<extra></extra>`,
    showlegend: false,
  })), [sampled]);

  const movingTraces = sampled.flatMap<Partial<Plotly.ScatterData>>(({ trace, seconds, xValues, yValues }) => {
    // Before a trace's first sample its signals have no value yet.
    const index = seconds.length > 0 && currentTime >= seconds[0] ? sampleIndexAt(seconds, currentTime) : -1;
    const at = <T,>(values: T[]) => index >= 0 ? [values[index]] : [];

    return [
      {
        type: "scatter",
        mode: "lines",
        x: xValues.slice(0, index + 1),
        y: yValues.slice(0, index + 1),
        line: { color: trace.color, width: 2, dash: trace.dash },
        hoverinfo: "skip",
        showlegend: false,
      },
      {
        type: "scatter",
        mode: "markers",
        name: trace.name,
        x: at(xValues),
        y: at(yValues),
        customdata: at(seconds),
        marker: { size: 12, color: trace.color, symbol: DOT_SYMBOLS[trace.dash], line: { color: "#ffffff", width: 2 } },
        hovertemplate: `${trace.axes.x}: %{x}<br>${trace.axes.y}: %{y}<br>t = %{customdata} s<extra></extra>`,
        showlegend: false,
      },
    ];
  });

  const axisLayout = (title: string): Partial<Plotly.LayoutAxis> => ({
    title: { text: title },
    gridcolor: GRID_COLOR,
    zerolinecolor: ZERO_LINE_COLOR,
    linecolor: ZERO_LINE_COLOR,
    tickfont: { color: AXIS_TEXT_COLOR },
  });

  const hasData = allSeconds.length > 0;

  return (
    <div className="cm-player">
      <div ref={wrapperRef} className="cm-graph-wrapper">
        <Plot
          data={[...pathTraces, ...movingTraces]}
          layout={{
            autosize: true,
            dragmode: "pan",
            hovermode: "closest",
            paper_bgcolor: "#ffffff",
            plot_bgcolor: "#ffffff",
            font: { color: "#121f19", family: "Inter, system-ui, sans-serif" },
            margin: { t: 20, l: 60, r: 20, b: 50 },
            showlegend: false,
            // Keep zoom and pan while playing, reset them when the axes change.
            uirevision: `${getAxisTitle(selection, "x")}|${getAxisTitle(selection, "y")}`,
            xaxis: axisLayout(getAxisTitle(selection, "x")),
            yaxis: axisLayout(getAxisTitle(selection, "y")),
          }}
          config={{ responsive: true }}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler={true}
        />
      </div>
      <div className="cm-player-controls">
        <Button
          icon={isPlaying ? IconNames.Pause : IconNames.Play}
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={togglePlaying}
          disabled={!hasData}
        />
        <Slider
          className="cm-player-slider"
          min={start}
          max={end > start ? end : start + 1}
          stepSize={(end - start) / SLIDER_STEPS || 1}
          value={currentTime}
          labelRenderer={false}
          onChange={(value) => {
            setIsPlaying(false);
            setTime(value);
          }}
          disabled={!hasData}
        />
        <span className="cm-player-time">t = {formatTime(currentTime)}</span>
        <HTMLSelect
          minimal
          aria-label="Playback speed"
          value={speed}
          options={SPEEDS.map((value) => ({ value, label: `${value}×` }))}
          onChange={(e) => setSpeed(Number(e.target.value))}
        />
      </div>
    </div>
  );
};

export default SimulationPlayer;
